import React, { useState, useEffect, useMemo, useCallback } from "react";
import {
  useNavigate,
  useLocation,
  useParams,
  useSearchParams
} from "react-router-dom";
import { useSelector } from "react-redux";
import { motion } from "framer-motion";
import { FaArrowLeft, FaRegFileAlt, FaSearch } from "react-icons/fa";
import { AiOutlineCloudUpload, AiOutlineDelete } from "react-icons/ai";
import {
  MdRefresh,
  MdAddCircleOutline,
  MdOutlineRemoveRedEye
} from "react-icons/md";
import { BiErrorCircle } from "react-icons/bi";
import "./UploadGdx.scss";
import HeaderNew from "common/components/custom/header-new/HeaderNew";
import HubHeader from "common/components/custom/hub-header/HubHeader";
import Table from "common/components/custom/sorting-table/SortingTable";
import CollapsibleDocumentList from "../../components/CollapsibleDocumentList";
import SimplePagination from "../../components/SimplePagination";
import AzureBlobModal from "../../components/AzureBlobModal";
import DocumentDetailView from "../../components/DocumentDetailView";
import DocumentApprovalPopup from "../../components/DocumentApprovalPopup";
import * as XLSX from "xlsx";
import { RootState } from "common/redux/core/root.reducer";
import {
  useGetDocumentsQuery,
  useGetUploadedDocumentsQuery,
  useGetGdxDocumentsQuery,
  useGetGdxDocumentsPaginatedQuery,
  useGetFlowConfigurationByIdQuery,
  useGetConfigurationDetailsQuery,
  useLazyGetDocumentsQuery,
  useLazyGetGdxDocumentDetailQuery
} from "../../data/api/queries/processing-hub-queries";
import { useDocumentSave } from "../../hooks/useDocumentSave";
import useProfile from "common/hooks/useProfile";
import {
  UserContext,
  EditableField
} from "../../data/models/document-extraction.model";
import {
  useUploadDocumentsMutation,
  useMultiUploadDocumentsMutation,
  useBatchUploadMutation,
  // useDeleteDocumentMutation,
  useDeleteGDXDocumentMutation,
  useUpdateGdxDocumentMutation,
  useRefreshDocumentsMutation,
  useCreateBatchMutation,
  useExportExtractedDataExcelMutation
} from "../../data/api/commands/processing-hub-commands";
import { ToastMessageUtil } from "common/components/base/toast-message/ToastMessage.util";
import { ToastVariant } from "common/enums/controls/toast.enum";
import {
  SearchDocumentsRequest,
  FlowConfiguration,
  ConfigurationDetails,
  DocumentTypeInformation
} from "../../data/models/processing-hub.model";
import { DocumentType } from "../../data/enums";
import { formattedDate } from "common/utils/date";
import CircularProgress from "@mui/material/CircularProgress";
import InsertDriveFileIcon from "@mui/icons-material/InsertDriveFile";
import CloudUploadIcon from "@mui/icons-material/CloudUpload";
import FolderIcon from "@mui/icons-material/Folder";
import AttachFileOutlinedIcon from "@mui/icons-material/AttachFileOutlined";
// Configuration constants
const UPLOAD_BATCH_SIZE = 20; // Maximum files per batch
const BATCH_DELAY_MS = 500; // Delay between batches in milliseconds

const UploadGDX: React.FC = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const flowConfig = location.state?.flowConfig as
    | DocumentTypeInformation
    | undefined;

  // Determine document type based on the document_type field from the flow config
  const documentType: "engineering" | "contract" | "invoice" | "others" =
    (flowConfig?.document_type || flowConfig?.name)?.toLowerCase() ===
    "engineering"
      ? "engineering"
      : (flowConfig?.document_type || flowConfig?.name)?.toLowerCase() ===
        "invoice"
      ? "invoice"
      : (flowConfig?.document_type || flowConfig?.name)?.toLowerCase() ===
        "others"
      ? "others"
      : "contract";

  useEffect(() => {
    if (documentType === "engineering") {
      navigate(`/processing-upload/${flowConfig?.flow_id}`, {
        state: { flowConfig, assignment: location.state?.assignment }
      });
    }
  }, [documentType, flowConfig, location.state?.assignment, navigate]);

  // Create back route with document type query parameter
  const getBackRouteWithQueryParam = (): string => {
    if (flowConfig?.document_type) {
      // Map the document type to the expected enum values
      let documentTypeParam = "";
      switch (flowConfig.document_type.toLowerCase()) {
        case "engineering":
          documentTypeParam = DocumentType.ENGINEERING;
          break;
        case "contract":
          documentTypeParam = DocumentType.CONTRACT;
          break;
        case "invoice":
          documentTypeParam = DocumentType.INVOICE;
          break;
        case "others":
          documentTypeParam = DocumentType.OTHERS;
          break;
        default:
          documentTypeParam = DocumentType.ENGINEERING;
      }
      return `/processing-hub?documentType=${documentTypeParam}`;
    }
    return "/processing-hub";
  };

  // Get current user information from Redux store
  const currentUser = useSelector((state: RootState) => state.user);

  // Get flow config ID from URL search parameters
  const { id } = useParams();
  const flowConfigId = id;
  const [files, setFiles] = useState<File[]>([]);
  const [searchTerm, setSearchTerm] = useState<string>("");
  const [currentFlowConfig, setCurrentFlowConfig] =
    useState<FlowConfiguration | null>(null);
  const [isDocumentDetailOpen, setIsDocumentDetailOpen] =
    useState<boolean>(false);
  const [isLoadingDocumentView, setIsLoadingDocumentView] =
    useState<boolean>(false);
  // In your component:
  const [isApprovalOpen, setIsApprovalOpen] = useState<boolean>(false);
  const [selectedDocumentData, setSelectedDocumentData] = useState<{
    fileName: string;
    documentType: string;
    documentLink: string;
    extractedFields: Array<{
      field: string;
      extractedValue: string;
      confidence: number;
    }>;
    flowId: string | number;
  } | null>(null);
  const [selectedDocumentDetailsData, setSelectedDocumentDetailsData] =
    useState<any | null>(null);
  const [configurationDetails, setConfigurationDetails] =
    useState<ConfigurationDetails | null>(null);
  const [isAzureBlobModalOpen, setIsAzureBlobModalOpen] =
    useState<boolean>(false);
  const [searchDocumentParams, setSearchDocumentParams] =
    useState<SearchDocumentsRequest>({
      searchTerm: "",
      page: 1,
      pageSize: 10
    });

  // State for folder vs file selection mode
  const [isFolderMode, setIsFolderMode] = useState<boolean>(false);

  // State for list view mode (table vs collapsible)
  const [isCollapsibleView, setIsCollapsibleView] = useState<boolean>(false);
  const [searchParams] = useSearchParams();

  // Get activeTab from URL if not provided as prop
  const currentActiveTab = searchParams.get("activeTab") || "";

  // Always request all documents regardless of approval state
  const getValidationStatus = (_activeTab: string): string => "";
  const showNotification = (
    message: string,
    type: "success" | "error" = "success"
  ) => {
    const variant =
      type === "error" ? ToastVariant.ERROR : ToastVariant.SUCCESS;
    ToastMessageUtil.show(message, variant);
  };
  // Pagination state
  const [paginationParams, setPaginationParams] = useState({
    page: 1,
    flowConfigId: flowConfigId || "",
    size: 10,
    sort: "",
    dir: "",
    search: "",
    showDuplicates: false,
    validation_status: getValidationStatus(currentActiveTab)
  });
  // Batch upload progress states
  const [batchUploadProgress, setBatchUploadProgress] = useState<{
    isUploading: boolean;
    currentBatch: number;
    totalBatches: number;
    totalFiles: number;
  }>({
    isUploading: false,
    currentBatch: 0,
    totalBatches: 0,
    totalFiles: 0
  });

  // Track current batch ID for updating batch status (API returns string ID)
  const [currentBatchId, setCurrentBatchId] = useState<string | null>(null);

  // State for handling approve/reject processing
  const [isApproving, setIsApproving] = useState(false);
  const [isRejecting, setIsRejecting] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isRefreshing, setIsRefreshing] = useState(false);

  // Initialize the save hook
  const { saveDocument, isSaving } = useDocumentSave({
    onSuccess: () => {
      showNotification("Data updated successfully");
    },
    onError: (error) => {
      showNotification(error, "error");
    }
  });

  const handleApprove = async () => {
    try {
      setIsApproving(true);

      // Update the validation status to Approved
      const flowId = selectedDocumentDetailsData?.flow_id || flowConfigId || "";
      const docId =
        selectedDocumentDetailsData?.id ||
        selectedDocumentDetailsData?.document_id ||
        "";

      const result = await updateGdxDocument({
        flowId,
        docId,
        updateData: { validation_status: "Approved" }
      }).unwrap();

      if (!result) {
        throw new Error("Failed to update validation status");
      }

      showNotification("Document approved successfully");

      // Refresh the documents list
      await refetchDocuments();
    } catch (error) {
      console.error("Approve error:", error);
      showNotification(
        error instanceof Error ? error.message : "Failed to approve document",
        "error"
      );
    } finally {
      setIsApproving(false);
    }
  };

  const handleReject = async () => {
    try {
      setIsRejecting(true);

      // Update the validation status to Rejected
      const flowId = selectedDocumentDetailsData?.flow_id || flowConfigId || "";
      const docId =
        selectedDocumentDetailsData?.id ||
        selectedDocumentDetailsData?.document_id ||
        "";

      const result = await updateGdxDocument({
        flowId,
        docId,
        updateData: { validation_status: "Rejected" }
      }).unwrap();

      if (!result) {
        throw new Error("Failed to update validation status");
      }

      showNotification("Document rejected successfully");

      // Refresh the documents list
      await refetchDocuments();
    } catch (error) {
      console.error("Reject error:", error);
      showNotification(
        error instanceof Error ? error.message : "Failed to reject document",
        "error"
      );
    } finally {
      setIsRejecting(false);
    }
  };

  const handleSubmit = async (
    currentConfig: any,
    editableFields: EditableField[],
    otherFields: any[],
    reextractedTokenCost: string | null,
    totalTokenCost: string
  ) => {
    try {
      setIsSubmitting(true);

      // Create user context
      const userContext: UserContext = {
        department: "IT",
        company: "admin",
        username:
          (currentUser.userPrincipalName?.includes("@")
            ? currentUser.userPrincipalName.split("@")[0]
            : undefined) ||
          (currentUser.mail?.includes("@")
            ? currentUser.mail.split("@")[0]
            : undefined) ||
          currentUser.displayName ||
          "Unknown User",
        role: "Extractor"
      };

      // First, save the current changes
      await saveDocument(
        currentConfig,
        editableFields,
        otherFields,
        userContext,
        reextractedTokenCost,
        totalTokenCost,
        selectedDocumentData?.flowId || "",
        currentConfig?.id || currentConfig?.document_id || ""
      );

      // Then update the validation status
      const flowId = selectedDocumentData?.flowId || "";
      const docId = currentConfig?.id || currentConfig?.document_id || "";

      const result = await updateGdxDocument({
        flowId,
        docId,
        updateData: { validation_status: "Approval Pending" }
      }).unwrap();

      if (!result) {
        throw new Error("Failed to update validation status");
      }

      showNotification("Document submitted for review successfully");

      // Refresh the document data
      await refetchDocuments();

      // Close the modal after successful submit
      setIsDocumentDetailOpen(false);
    } catch (error) {
      console.error("Submit error:", error);
      showNotification(
        error instanceof Error ? error.message : "Failed to submit document",
        "error"
      );
    } finally {
      setIsSubmitting(false);
    }
  };

  // Fetch flow configuration by ID if flowConfigId is present
  const {
    data: flowConfigData,
    isLoading: isLoadingFlowConfig,
    error: flowConfigError
  } = useGetFlowConfigurationByIdQuery(flowConfigId!, {
    skip: !flowConfigId
  });

  // Fetch configuration details using config_id from flowConfigData
  const {
    data: configDetailsData,
    isLoading: isLoadingConfigDetails,
    error: configDetailsError
  } = useGetConfigurationDetailsQuery(flowConfigData?.config_id || "", {
    skip: !flowConfigData?.config_id
  });

  const {
    data: documentsData,
    isLoading: isLoadingDocuments,
    error: documentsError,
    refetch: refetchDocuments
  } = useGetGdxDocumentsPaginatedQuery(paginationParams);

  // Update current flow config when data is fetched
  useEffect(() => {
    if (flowConfigData) {
      setCurrentFlowConfig(flowConfigData);
    }
  }, [flowConfigData]);

  // Update configuration details when data is fetched
  useEffect(() => {
    if (configDetailsData) {
      setConfigurationDetails(configDetailsData);
    }
  }, [configDetailsData]);

  // Log any flow config errors
  useEffect(() => {
    if (flowConfigError) {
      console.error("Flow config error:", flowConfigError);
    }
  }, [flowConfigError]);

  // Log any configuration details errors
  useEffect(() => {
    if (configDetailsError) {
      console.error("Configuration details error:", configDetailsError);
    }
  }, [configDetailsError]);

  const { displayName, mail, userPrincipalName } = useProfile();
  const [uploadDocuments, { isLoading: isUploading }] =
    useUploadDocumentsMutation();
  const [multiUploadDocuments, { isLoading: isMultiUploading }] =
    useMultiUploadDocumentsMutation();
  const [batchUpload, { isLoading: isBatchUploading }] =
    useBatchUploadMutation();
  const [deleteGDXDocument] = useDeleteGDXDocumentMutation();
  const [updateGdxDocument] = useUpdateGdxDocumentMutation();
  const [exportExtractedDataExcel] = useExportExtractedDataExcelMutation();
  const [refreshDocuments] = useRefreshDocumentsMutation();
  const [createBatch] = useCreateBatchMutation();

  const resolveFlowId = (): number | undefined => {
    if (flowConfig?.flow_id) {
      return flowConfig.flow_id;
    }
    if (currentFlowConfig?.id) {
      return currentFlowConfig.id;
    }
    const parsedFlowId = flowConfigId ? Number(flowConfigId) : Number.NaN;
    return Number.isFinite(parsedFlowId) ? parsedFlowId : undefined;
  };

  const resolveDocumentTypeName = (): string => {
    return (
      flowConfig?.name ||
      flowConfig?.document_type ||
      currentFlowConfig?.flow_name ||
      currentFlowConfig?.flow_description ||
      configurationDetails?.config_name ||
      ""
    );
  };

  // Lazy query for fetching document details
  const [
    getGdxDocumentDetail,
    {
      data: documentDetailData,
      isLoading: isLoadingDocumentDetail,
      error: documentDetailError
    }
  ] = useLazyGetGdxDocumentDetailQuery();

  const generateColorFromName = useCallback((name: string): string => {
    let hash = 0;
    for (let i = 0; i < name.length; i++) {
      const char = name.charCodeAt(i);
      hash = (hash << 5) - hash + char;
      hash = hash & hash; // Convert to 32-bit integer
    }

    // Generate a bright, readable color
    const hue = Math.abs(hash) % 360;
    const saturation = 65 + (Math.abs(hash) % 20); // 65-85%
    const lightness = 45 + (Math.abs(hash) % 20); // 45-65%

    // Convert HSL to hex
    const hslToHex = (h: number, s: number, l: number): string => {
      l /= 100;
      const a = (s * Math.min(l, 1 - l)) / 100;
      const f = (n: number) => {
        const k = (n + h / 30) % 12;
        const color = l - a * Math.max(Math.min(k - 3, 9 - k, 1), -1);
        return Math.round(255 * color)
          .toString(16)
          .padStart(2, "0");
      };
      return `#${f(0)}${f(8)}${f(4)}`;
    };

    return hslToHex(hue, saturation, lightness);
  }, []);

  // Transform API response to match DocumentTable interface
  const transformDocumentsData = useCallback(
    (apiData: any) => {
      // Handle different API response structures
      let dataArray = [];

      if (Array.isArray(apiData)) {
        dataArray = apiData;
      } else if (apiData && Array.isArray(apiData.data)) {
        dataArray = apiData.data;
      } else if (apiData && Array.isArray(apiData.items)) {
        dataArray = apiData.items;
      } else if (apiData && Array.isArray(apiData.documents)) {
        dataArray = apiData.documents;
      } else if (apiData && Array.isArray(apiData.result)) {
        dataArray = apiData.result;
      } else if (apiData && Array.isArray(apiData.results)) {
        dataArray = apiData.results;
      } else {
        console.warn("GDX API response is not in expected format:", apiData);
        return [];
      }

      return (
        dataArray
          // .filter((doc: any) => doc.config_id === configurationDetails?.id)
          .map((doc: any, index: number) => ({
            id: doc.documentGuid || doc.id || index + 1,
            documentGuid: doc.documentGuid || doc.id || doc.document_id,
            fileName:
              doc.name ||
              doc.document_name ||
              doc.original_filename ||
              "Unknown File",
            flowName: doc.gpName || doc.flowName || doc.flow || "N/A",
            flowId: doc.flowId || doc.flow_id,
            docType:
              doc.heading ||
              doc.docType ||
              doc.type ||
              doc.document_type ||
              "Document",
            documentType:
              doc.FileCategory ||
              doc.fileCategory ||
              doc.file_category ||
              doc.category ||
              "",
            uploadedDate: doc.uploaded_on
              ? formattedDate(doc.uploaded_on)
              : doc.uploadedDate || formattedDate(new Date().toISOString()),
            updatedDate: doc.updated_on
              ? formattedDate(doc.updated_on)
              : doc.uploadedDate || formattedDate(new Date().toISOString()),
            uploadedBy:
              doc.createdBy || doc.uploaded_by
                ? {
                    initial: (doc.createdBy || doc.uploaded_by)
                      .charAt(0)
                      .toUpperCase(),
                    name: doc.createdBy || doc.uploaded_by,
                    color: "#C5B3E6" // Fixed color for now to avoid circular dependency
                  }
                : undefined,
            activityStatus: `Version ${
              doc.versionNumber || doc.version || "1"
            }`,
            status: doc.status || doc.extraction_status || "Available",
            validation_status:
              doc.validation_status || doc.approval_status || "Pending",
            batch_id: doc.batch_id || null // Extract batch_id from API response
          }))
      );
    },
    [configurationDetails?.id]
  );

  // Helper function to reset file input and clear files
  const resetFileInput = () => {
    setFiles([]);
    const fileInput = document.getElementById(
      "file-upload"
    ) as HTMLInputElement;
    if (fileInput) {
      fileInput.value = "";
    }
  };

  const handleFileChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    const fileList = event.target.files;
    if (fileList) {
      const allFiles = Array.from(fileList);

      // Filter out directories and only keep files
      const validFiles = allFiles.filter((file) => {
        // Check if it's actually a file (has size or type)
        return file.size > 0 || file.type !== "";
      });

      if (validFiles.length > 0) {
        setFiles(validFiles);
      } else {
        console.warn("No valid files found in the selected folder");
      }
    }
  };

  const handleUploadClick = (e: React.MouseEvent) => {
    const uploadMethod = currentFlowConfig?.upload_method || "manual"; // Default to manual

    switch (uploadMethod) {
      case "blob":
        setIsAzureBlobModalOpen(true);
        break;
      case "sharepoint":
        // TODO: Show SharePoint picker
        break;
      case "manual":
      default: {
        // Present choice between files or folder
        e.preventDefault();
        e.stopPropagation();
        break;
      }
    }
  };

  const handleFileUpload = () => {
    setIsFolderMode(false);
    resetFileInput();
    setTimeout(() => {
      const fileInput = document.getElementById(
        "file-upload"
      ) as HTMLInputElement;
      if (fileInput) {
        fileInput.click();
      }
    }, 100);
  };

  const handleFolderUpload = () => {
    setIsFolderMode(true);
    resetFileInput();
    setTimeout(() => {
      const fileInput = document.getElementById(
        "file-upload"
      ) as HTMLInputElement;
      if (fileInput) {
        fileInput.click();
      }
    }, 100);
  };

  const handleAzureBlobUpload = async (blobFiles: File[]) => {
    if (blobFiles.length === 0) {
      return;
    }

    try {
      // Add all files to the upload queue immediately
      setFiles((prevFiles) => [...prevFiles, ...blobFiles]);
      setIsAzureBlobModalOpen(false);
    } catch (error) {
      console.error("Error handling Azure Blob upload:", error);
    }
  };

  const handleSearch = (event: React.ChangeEvent<HTMLInputElement>) => {
    const term = event.target.value;
    setSearchTerm(term);

    // Update pagination params with search term and reset to page 1
    setPaginationParams((prev) => ({
      ...prev,
      search: term,
      page: 1
    }));

    setSearchDocumentParams((prev) => ({
      ...prev,
      searchTerm: term,
      page: 1
    }));
  };

  const handleRefresh = async () => {
    try {
      setIsRefreshing(true);
      await refetchDocuments();
      // Trigger batch refresh for progress bar updates
      window.dispatchEvent(new CustomEvent("refreshBatches"));
    } catch (error) {
      console.error("Error refreshing documents:", error);
    } finally {
      setIsRefreshing(false);
    }
  };

  const handlePageChange = (page: number) => {
    setPaginationParams((prev) => ({
      ...prev,
      page: page
    }));
  };

  const handlePageSizeChange = (
    event: React.ChangeEvent<HTMLSelectElement>
  ) => {
    const newSize = parseInt(event.target.value);
    setPaginationParams((prev) => ({
      ...prev,
      size: newSize,
      page: 1 // Reset to first page when changing page size
    }));
  };

  const handleSort = (field: string) => {
    setPaginationParams((prev) => {
      // Toggle direction if same field, otherwise start with 'asc'
      const newDir = prev.sort === field && prev.dir === "asc" ? "desc" : "asc";
      return {
        ...prev,
        sort: field,
        dir: newDir,
        page: 1 // Reset to first page when sorting
      };
    });
  };

  // Helper function to create batch before upload
  const createBatchBeforeUpload = async (
    batchFiles: File[],
    batchNumber?: number
  ) => {
    // Use currentFlowConfig (from API) as fallback when flowConfig (from location.state) is undefined
    const activeFlowConfig = flowConfig || currentFlowConfig;

    if (!activeFlowConfig) {
      console.warn("❌ No flow configuration available for batch creation");
      setCurrentBatchId(null);
      return null;
    }

    try {
      const startTime = new Date().toISOString();
      const docTypeForBatch =
        flowConfig?.document_type || currentFlowConfig?.flow_name || "unknown";
      const timestamp = new Date().toISOString().slice(0, 16); // Format: 2025-11-06T23:14
      const batchSuffix = batchNumber ? `_batch${batchNumber}` : "";
      const batchName = `${docTypeForBatch}_${timestamp}${batchSuffix}`;

      const batchRequest = {
        batch_name: batchName,
        start_time: startTime,
        end_time: startTime, // Will be updated later
        config_id: activeFlowConfig?.config_id || 0,
        flow_id: flowConfig?.flow_id || currentFlowConfig?.id || 0,
        files_count: batchFiles.length,
        status: "in_progress",
        created_by:
          (currentUser.userPrincipalName?.includes("@")
            ? currentUser.userPrincipalName.split("@")[0]
            : undefined) ||
          (currentUser.mail?.includes("@")
            ? currentUser.mail.split("@")[0]
            : undefined) ||
          currentUser.displayName ||
          "Unknown User"
      };

      const batchResponse = await createBatch(batchRequest).unwrap();

      // Store the batch ID for later use (API returns 'id' field, not 'batch_id')
      if (batchResponse && batchResponse.id) {
        setCurrentBatchId(batchResponse.id);
        return batchResponse.id;
      } else {
        setCurrentBatchId(null);
        return null;
      }
    } catch (batchError) {
      setCurrentBatchId(null);
      return null;
    }
  };

  const handleUpload = async () => {
    if (files.length === 0) {
      return;
    }

    try {
      const configName =
        configurationDetails?.config_name || "Contract Document";
      const modelName = configurationDetails?.extraction_model;
      const resolvedDocumentTypeName = resolveDocumentTypeName();
      const isEngineeringFlow = /engineering/i.test(resolvedDocumentTypeName);
      const flowId = resolveFlowId();
      const totalBatches = Math.ceil(files.length / UPLOAD_BATCH_SIZE);

      // Initialize batch upload progress
      setBatchUploadProgress({
        isUploading: true,
        currentBatch: 0,
        totalBatches: totalBatches,
        totalFiles: files.length
      });

      // Add a small delay to ensure UI updates
      await new Promise((resolve) => setTimeout(resolve, 100));

      // If files are within batch size limit, upload in a single batch
      if (files.length <= UPLOAD_BATCH_SIZE) {
        setBatchUploadProgress((prev) => ({ ...prev, currentBatch: 1 }));

        // Add another small delay to show progress update
        await new Promise((resolve) => setTimeout(resolve, 200));

        if (isEngineeringFlow) {
          const formData = new FormData();
          files.forEach((file) => formData.append("files", file, file.name));

          await multiUploadDocuments({
            files: formData,
            gpName: "BULKUPLOAD",
            isReferenceDocument: true,
            referenceType: "GP",
            flowId: flowId,
            documentType: resolvedDocumentTypeName
          }).unwrap();
        } else {
          // Create batch before upload
          const batchId = await createBatchBeforeUpload(files);

          // Use new batch upload API
          await batchUpload({
            uploadRequest: {
              flow_id: Number(flowConfigId),
              files: files,
              model_name: modelName || "",
              chat_enabled: configurationDetails?.chat_enabled ?? false,
              batch_size: 10,
              batch_status: "In Progress"
            },
            userEmail: userPrincipalName
          }).unwrap();
        }
      } else {
        // Process files in batches
        let masterBatchId: string | null = null;
        if (!isEngineeringFlow) {
          // Create ONE batch for ALL files at the beginning
          masterBatchId = await createBatchBeforeUpload(files);
        }

        let successfulBatches = 0;
        let failedBatches = 0;

        for (let i = 0; i < totalBatches; i++) {
          const startIndex = i * UPLOAD_BATCH_SIZE;
          const endIndex = Math.min(
            startIndex + UPLOAD_BATCH_SIZE,
            files.length
          );
          const batchFiles = files.slice(startIndex, endIndex);

          // Update progress before starting batch
          setBatchUploadProgress((prev) => ({ ...prev, currentBatch: i + 1 }));

          // Add delay to ensure UI updates
          await new Promise((resolve) => setTimeout(resolve, 300));

          try {
            let result;
            if (isEngineeringFlow) {
              const formData = new FormData();
              batchFiles.forEach((file) =>
                formData.append("files", file, file.name)
              );

              result = await multiUploadDocuments({
                files: formData,
                gpName: "BULKUPLOAD",
                isReferenceDocument: true,
                referenceType: "GP",
                flowId: flowId,
                documentType: resolvedDocumentTypeName
              }).unwrap();
            } else {
              // Use the master batch ID for all upload chunks
              // Use new batch upload API
              result = await batchUpload({
                uploadRequest: {
                  flow_id: Number(flowConfigId),
                  files: batchFiles,
                  model_name: modelName || "",
                  chat_enabled: configurationDetails?.chat_enabled ?? false,
                  batch_size: 10,
                  batch_status: "In Progress"
                },
                userEmail: userPrincipalName
              }).unwrap();
            }

            successfulBatches++;
          } catch (batchError) {
            failedBatches++;
            console.error(`❌ Error uploading batch ${i + 1}:`, batchError);

            // For now, continue with other batches even if one fails
            // You can modify this behavior based on requirements
          }

          // Add delay between batches to prevent overwhelming the server
          if (i < totalBatches - 1) {
            await new Promise((resolve) => setTimeout(resolve, BATCH_DELAY_MS));
          }
        }

        if (failedBatches > 0) {
          console.warn(
            `⚠️ ${failedBatches} out of ${totalBatches} batches failed to upload`
          );
          // You could show a notification to the user here
        }
      }

      // Clear files and reset input after upload attempt
      resetFileInput();

      // Keep progress visible for a moment before hiding
      await new Promise((resolve) => setTimeout(resolve, 2000));

      // Reset batch upload progress
      setBatchUploadProgress({
        isUploading: false,
        currentBatch: 0,
        totalBatches: 0,
        totalFiles: 0
      });

      // Add a small delay to ensure server processing is complete
      await new Promise((resolve) => setTimeout(resolve, 1000));

      // Refetch the documents to show the newly uploaded files
      await refetchDocuments();
    } catch (error) {
      console.error("❌ Error uploading documents:", error);

      // Clear files and reset input on error too
      resetFileInput();

      // Reset batch upload progress on error
      setBatchUploadProgress({
        isUploading: false,
        currentBatch: 0,
        totalBatches: 0,
        totalFiles: 0
      });
    }
  };

  const handleDeleteDocument = async (documentId: string | number) => {
    try {
      await deleteGDXDocument({ documentId, flowId: flowConfigId! }).unwrap();
      // Refresh the table after successful deletion
      await refetchDocuments();
    } catch (error) {
      console.error("Error deleting document:", error);
    }
  };

  const handleEditDocument = (documentId: string | number) => {
    const document = documents.find(
      (doc: any) => doc.id === documentId || doc.documentGuid === documentId
    );
    const documentGuid = document?.documentGuid || documentId;
    navigate(`/documents/${documentGuid}?page=1&size=10`, {
      state: {
        path: "/processing-hub",
        useEngApi: documentType === "engineering",
        editMode: true
      }
    });
  };

  const handleViewDocument = async (documentId: string | number) => {
    try {
      // Show loader
      setIsLoadingDocumentView(true);

      // Show loading state immediately
      const loadingDocumentData = {
        fileName: "Loading...",
        documentType: "Loading...",
        documentLink: "Loading...",
        extractedFields: [
          {
            field: "Loading",
            extractedValue: "Fetching document details...",
            confidence: 0
          }
        ],
        flowId: flowConfig?.flow_id || 0
      };

      setSelectedDocumentData(loadingDocumentData);
      // setIsDocumentDetailOpen(true);

      // Get flowId from currentFlowConfig or from the document in the documents array
      const document = documents.find(
        (doc: any) => doc.id === documentId || doc.documentGuid === documentId
      );
      const flowId =
        document?.flowId || currentFlowConfig?.id || flowConfig?.flow_id || 0;

      // Fetch the actual document detail using the API
      const result = await getGdxDocumentDetail({ documentId, flowId });

      if (result.data) {
        const apiData = result.data;

        // Process the extracteddata array from the new API response
        let extractedFields: Array<{
          field: string;
          extractedValue: string;
          confidence: number;
          fieldId?: number;
          dataType?: string;
          validationRules?: string;
          uiPositionOrder?: number;
        }> = [];

        // Map extracteddata array to extractedFields
        if (apiData.extracteddata && Array.isArray(apiData.extracteddata)) {
          extractedFields = apiData.extracteddata.map((item) => {
            // Determine the actual value based on data_type
            let extractedValue = "";
            if (item.value_text !== null) {
              extractedValue = item.value_text;
            } else if (item.value_number !== null) {
              extractedValue = String(item.value_number);
            } else if (item.value_date !== null) {
              extractedValue = item.value_date;
            }

            return {
              field: item.field_name || "Unknown Field",
              extractedValue: extractedValue || "N/A",
              confidence: item.confidence_score || 0,
              fieldId: item.field_id,
              dataType: item.data_type,
              validationRules: item.validation_rules,
              uiPositionOrder: item.ui_position_order
            };
          });

          // Sort by ui_position_order if available
          extractedFields.sort(
            (a, b) => (a.uiPositionOrder || 0) - (b.uiPositionOrder || 0)
          );
        }

        // If no extracted fields from API, create basic document info fields
        if (extractedFields.length === 0) {
          extractedFields = [
            {
              field: "Document Type",
              extractedValue: apiData.document_type || "N/A",
              confidence: apiData.document_type ? 100 : 0
            },
            {
              field: "Extraction Status",
              extractedValue: apiData.extraction_status || "N/A",
              confidence: apiData.extraction_status ? 100 : 0
            },
            {
              field: "Validation Status",
              extractedValue: apiData.validation_status || "N/A",
              confidence: apiData.validation_status ? 100 : 0
            },
            {
              field: "Uploaded By",
              extractedValue: apiData.uploaded_by || "N/A",
              confidence: 100
            },
            {
              field: "Uploaded On",
              extractedValue: apiData.uploaded_on || "N/A",
              confidence: apiData.uploaded_on ? 100 : 0
            }
          ];

          if (apiData.error_message) {
            extractedFields.push({
              field: "Error Message",
              extractedValue: apiData.error_message,
              confidence: 100
            });
          }
        }

        // Transform the API response to match the DocumentDetailView expected format
        const documentData = {
          fileName:
            apiData.document_name ||
            apiData.original_filename ||
            "Unknown File",
          documentType: apiData.document_type || "Document",
          extractedFields: extractedFields,
          documentLink: apiData.document_link || "N/A",
          flowId: flowId
        };

        setSelectedDocumentData(documentData);
        // Add flow_id to apiData before setting it
        setSelectedDocumentDetailsData({ ...apiData, flow_id: flowId });
        setIsLoadingDocumentView(false);
        setIsDocumentDetailOpen(true);
      } else {
        console.error("Failed to fetch document details");
        // Fallback to mock data if API fails
        const mockDocumentData = {
          fileName: "Document",
          documentType: "Contract Document",
          documentLink: "N/A",
          extractedFields: [
            {
              field: "Error",
              extractedValue: "No data returned from API",
              confidence: 0
            }
          ],
          flowId: flowConfig?.flow_id || 0
        };

        setSelectedDocumentData(mockDocumentData);
        setIsLoadingDocumentView(false);
        setIsDocumentDetailOpen(true);
      }
    } catch (error) {
      console.error("Error fetching document details:", error);

      // Fallback to error data on exception
      const errorDocumentData = {
        fileName: "Error Loading Document",
        documentType: "Error",
        documentLink: "N/A",
        extractedFields: [
          {
            field: "Error",
            extractedValue: `Failed to load document details: ${
              error instanceof Error ? error.message : "Unknown error"
            }`,
            confidence: 0
          }
        ],
        flowId: flowConfig?.flow_id || 0
      };

      setSelectedDocumentData(errorDocumentData);
      setIsLoadingDocumentView(false);
      setIsDocumentDetailOpen(true);
    }
  };

  const handleApproveViewDocument = async (documentId: string | number) => {
    try {
      // Show loading state immediately
      const loadingDocumentData = {
        fileName: "Loading...",
        documentType: "Loading...",
        documentLink: "Loading...",
        extractedFields: [
          {
            field: "Loading",
            extractedValue: "Fetching document details...",
            confidence: 0
          }
        ],
        flowId: flowConfig?.flow_id || 0
      };

      setSelectedDocumentData(loadingDocumentData);
      setIsApprovalOpen(true);

      // Get flowId from currentFlowConfig or from the document in the documents array
      const document = documents.find(
        (doc: any) => doc.id === documentId || doc.documentGuid === documentId
      );
      const flowId =
        document?.flowId || currentFlowConfig?.id || flowConfig?.flow_id || 0;

      // Fetch the actual document detail using the API
      const result = await getGdxDocumentDetail({ documentId, flowId });

      if (result.data) {
        const apiData = result.data;

        // Process the extracteddata array from the new API response
        let extractedFields: Array<{
          field: string;
          extractedValue: string;
          confidence: number;
          fieldId?: number;
          dataType?: string;
          validationRules?: string;
          uiPositionOrder?: number;
        }> = [];

        // Map extracteddata array to extractedFields
        if (apiData.extracteddata && Array.isArray(apiData.extracteddata)) {
          extractedFields = apiData.extracteddata.map((item) => {
            // Determine the actual value based on data_type
            let extractedValue = "";
            if (item.value_text !== null) {
              extractedValue = item.value_text;
            } else if (item.value_number !== null) {
              extractedValue = String(item.value_number);
            } else if (item.value_date !== null) {
              extractedValue = item.value_date;
            }

            return {
              field: item.field_name || "Unknown Field",
              extractedValue: extractedValue || "N/A",
              confidence: item.confidence_score || 0,
              fieldId: item.field_id,
              dataType: item.data_type,
              validationRules: item.validation_rules,
              uiPositionOrder: item.ui_position_order
            };
          });

          // Sort by ui_position_order if available
          extractedFields.sort(
            (a, b) => (a.uiPositionOrder || 0) - (b.uiPositionOrder || 0)
          );
        }

        // If no extracted fields from API, create basic document info fields
        if (extractedFields.length === 0) {
          extractedFields = [
            {
              field: "Document Type",
              extractedValue: apiData.document_type || "N/A",
              confidence: apiData.document_type ? 100 : 0
            },
            {
              field: "Extraction Status",
              extractedValue: apiData.extraction_status || "N/A",
              confidence: apiData.extraction_status ? 100 : 0
            },
            {
              field: "Validation Status",
              extractedValue: apiData.validation_status || "N/A",
              confidence: apiData.validation_status ? 100 : 0
            },
            {
              field: "Uploaded By",
              extractedValue: apiData.uploaded_by || "N/A",
              confidence: 100
            },
            {
              field: "Uploaded On",
              extractedValue: apiData.uploaded_on || "N/A",
              confidence: apiData.uploaded_on ? 100 : 0
            }
          ];

          if (apiData.error_message) {
            extractedFields.push({
              field: "Error Message",
              extractedValue: apiData.error_message,
              confidence: 100
            });
          }
        }

        // Transform the API response to match the DocumentApprovalPopup expected format
        const documentData = {
          fileName:
            apiData.document_name ||
            apiData.original_filename ||
            "Unknown File",
          documentLink: apiData.document_link || "N/A",
          documentType: apiData.document_type || "Document",
          extractedFields: extractedFields,
          flowId: flowId
        };

        setSelectedDocumentData(documentData);
        // Add flow_id to apiData before setting it
        setSelectedDocumentDetailsData({ ...apiData, flow_id: flowId });
      } else {
        console.error("Failed to fetch document details for approval");
        // Fallback to mock data if API fails
        const mockDocumentData = {
          fileName: "Document",
          documentType: "Contract Document",
          documentLink: "N/A",
          extractedFields: [
            {
              field: "Error",
              extractedValue: "No data returned from API",
              confidence: 0
            }
          ],
          flowId: flowConfig?.flow_id || 0
        };

        setSelectedDocumentData(mockDocumentData);
      }
    } catch (error) {
      console.error("Error fetching document details for approval:", error);

      // Fallback to error data on exception
      const errorDocumentData = {
        fileName: "Error Loading Document",
        documentType: "Error",
        documentLink: "N/A",
        extractedFields: [
          {
            field: "Error",
            extractedValue: `Failed to load document details: ${
              error instanceof Error ? error.message : "Unknown error"
            }`,
            confidence: 0
          }
        ],
        flowId: flowConfig?.flow_id || 0
      };

      setSelectedDocumentData(errorDocumentData);
    }
  };

  // Auto-update document detail modal when documentDetailData changes (e.g., after refetch)
  useEffect(() => {
    if (documentDetailData && isDocumentDetailOpen) {
      const apiData = documentDetailData;

      // Process the extracteddata array from the API response
      let extractedFields: Array<{
        field: string;
        extractedValue: string;
        confidence: number;
        fieldId?: number;
        dataType?: string;
        validationRules?: string;
        uiPositionOrder?: number;
      }> = [];

      // Map extracteddata array to extractedFields
      if (apiData.extracteddata && Array.isArray(apiData.extracteddata)) {
        extractedFields = apiData.extracteddata.map((item) => {
          // Determine the actual value based on data_type
          let extractedValue = "";
          if (item.value_text !== null) {
            extractedValue = item.value_text;
          } else if (item.value_number !== null) {
            extractedValue = String(item.value_number);
          } else if (item.value_date !== null) {
            extractedValue = item.value_date;
          }

          return {
            field: item.field_name || "Unknown Field",
            extractedValue: extractedValue || "N/A",
            confidence: item.confidence_score || 0,
            fieldId: item.field_id,
            dataType: item.data_type,
            validationRules: item.validation_rules,
            uiPositionOrder: item.ui_position_order
          };
        });

        // Sort by ui_position_order if available
        extractedFields.sort(
          (a, b) => (a.uiPositionOrder || 0) - (b.uiPositionOrder || 0)
        );
      }

      // Transform the API response to match the DocumentDetailView expected format
      const documentData = {
        fileName:
          apiData.document_name || apiData.original_filename || "Unknown File",
        documentType: apiData.document_type || "Document",
        documentLink: apiData.document_link || "N/A",
        extractedFields: extractedFields,
        flowId:
          (apiData as any).flow_id ||
          currentFlowConfig?.id ||
          flowConfig?.flow_id ||
          0
      };

      setSelectedDocumentData(documentData);
      setSelectedDocumentDetailsData({
        ...apiData,
        flow_id:
          (apiData as any).flow_id ||
          currentFlowConfig?.id ||
          flowConfig?.flow_id ||
          0
      });
    }
  }, [
    documentDetailData,
    isDocumentDetailOpen,
    currentFlowConfig?.id,
    flowConfig?.flow_id
  ]);

  // Transform documents data if available, otherwise return empty array
  const documents = useMemo(() => {
    if (!documentsData || documentsError) {
      return [];
    }

    const transformedDocuments = transformDocumentsData(
      documentsData.data || documentsData
    );

    // Filter documents by current flow ID
    if (!flowConfig?.flow_id) {
      return transformedDocuments; // If no flowId, show all documents
    }
    return transformedDocuments.filter((doc: any) => {
      // Convert both to strings for comparison to handle different types
      const docFlowId = doc.flowId?.toString();
      const currentFlowIdStr = flowConfig.flow_id.toString();
      return docFlowId === currentFlowIdStr;
    });
  }, [
    documentsData,
    documentsError,
    transformDocumentsData,
    flowConfig?.flow_id
  ]);
  const handleDownloadDocument = async (documentId: string | number) => {
    try {
      // Call the export API
      const result = await exportExtractedDataExcel(documentId);

      if ("error" in result || !result.data) {
        ToastMessageUtil.show(
          "Failed to export document to Excel",
          ToastVariant.ERROR
        );
        return;
      }

      // Create a blob from the response
      const blob = result.data;

      // Create a download link and trigger download
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement("a");
      link.href = url;

      // Get the actual document name
      const documentData = documents.find(
        (doc: any) => doc.id === documentId || doc.documentGuid === documentId
      );
      const docName = documentData?.fileName || "document";
      // Remove file extension and sanitize the filename
      const sanitizedName = docName
        .replace(/\.[^/.]+$/, "") // Remove extension
        .replace(/[^a-z0-9]/gi, "_") // Replace special chars with underscore
        .toLowerCase();

      // Generate file name with timestamp
      const fileName = `${sanitizedName}_extracted_data_${
        new Date().toISOString().split("T")[0]
      }.xlsx`;
      link.setAttribute("download", fileName);

      // Trigger download
      document.body.appendChild(link);
      link.click();

      // Cleanup
      link.parentNode?.removeChild(link);
      window.URL.revokeObjectURL(url);

      ToastMessageUtil.show(
        "Excel file downloaded successfully",
        ToastVariant.SUCCESS
      );
    } catch (error) {
      console.error("Error exporting document to Excel:", error);
      ToastMessageUtil.show(
        `Failed to export document to Excel: ${
          error instanceof Error ? error.message : "Unknown error"
        }`,
        ToastVariant.ERROR
      );
    }
  };

  return (
    <>
      <HeaderNew showSearch={false} />
      <div className="processing-upload-container">
        {/* Header */}
        <HubHeader
          className="header"
          title="Processing Hub"
          description="Document upload and extraction validation"
          showBackButton={true}
          backRoute={getBackRouteWithQueryParam()}
        />

        {/* Main Content */}
        <div className="upload-content">
          {/* Upload Header */}
          {currentActiveTab !== "approval" &&
            currentActiveTab !== "completed" && (
              <div className="upload-header">
                <h2>Upload new document</h2>
              </div>
            )}
          <div className="document-list-wrapper">
            {currentActiveTab !== "approval" &&
              currentActiveTab !== "completed" && (
                <div className="upload-area">
                  <div className="upload-options">
                    {/* File Upload Option */}
                    <div className="upload-option" onClick={handleFileUpload}>
                      <div className="upload-box">
                        <div className="upload-icon">
                          <AttachFileOutlinedIcon className="upload-file-icon" />
                        </div>
                      </div>
                      <div className="upload-text">
                        <span className="upload-title">Select Files</span>
                        <span className="upload-description">
                          Choose individual files to upload
                        </span>
                      </div>
                    </div>

                    {/* Folder Upload Option */}
                    <div className="upload-option" onClick={handleFolderUpload}>
                      <div className="upload-box">
                        <div className="upload-icon">
                          <FolderIcon className="upload-folder-icon" />
                        </div>
                      </div>
                      <div className="upload-text">
                        <span className="upload-title">Select Folder</span>
                        <span className="upload-description">
                          Choose a folder to upload all files and subfolders
                        </span>
                      </div>
                    </div>
                  </div>

                  {/* Hidden file input */}
                  <input
                    type="file"
                    id="file-upload"
                    key={`file-input-${isFolderMode ? "folder" : "file"}`}
                    multiple
                    {...(isFolderMode ? { webkitdirectory: "" } : {})}
                    onChange={handleFileChange}
                    style={{ display: "none" }}
                  />
                  {/* <div className="formats">
                SVG, PNG, JPG, PDF, DOC, DOCX or GIF (max. 10MB per file)
              </div> */}

                  {/* Display selected files */}
                  {files.length > 0 && (
                    <div className="selected-files">
                      <h4>Selected Files ({files.length}):</h4>
                      {files.length > 0 && files[0].webkitRelativePath && (
                        <div className="folder-info">
                          <p>
                            <strong>Folder:</strong>{" "}
                            {files[0].webkitRelativePath.split("/")[0]}
                          </p>
                          <p>
                            <strong>Total files found:</strong> {files.length}{" "}
                            files from folder and subfolders
                          </p>
                          <button
                            type="button"
                            className="clear-selection-btn"
                            onClick={() => resetFileInput()}
                            style={{
                              marginTop: "8px",
                              padding: "4px 8px",
                              fontSize: "12px",
                              backgroundColor: "#f87171",
                              color: "white",
                              border: "none",
                              borderRadius: "4px",
                              cursor: "pointer"
                            }}
                          >
                            Clear Selection
                          </button>
                        </div>
                      )}
                      {files.length > UPLOAD_BATCH_SIZE && (
                        <div className="batch-info-notice">
                          <p>
                            <strong>Notice:</strong> You have selected{" "}
                            {files.length} files. Files will be uploaded in
                            batches of {UPLOAD_BATCH_SIZE} for optimal
                            performance.
                          </p>
                        </div>
                      )}
                      <ul>
                        {files.map((file, index) => (
                          <li key={index} className="file-item">
                            <span className="file-name">
                              {file.webkitRelativePath || file.name}
                            </span>
                            <span className="file-size">
                              ({(file.size / 1024 / 1024).toFixed(2)} MB)
                            </span>
                            <button
                              type="button"
                              className="remove-file"
                              onClick={() => {
                                const newFiles = files.filter(
                                  (_, i) => i !== index
                                );
                                setFiles(newFiles);

                                // If no files left, reset the input completely
                                if (newFiles.length === 0) {
                                  const fileInput = document.getElementById(
                                    "file-upload"
                                  ) as HTMLInputElement;
                                  if (fileInput) {
                                    fileInput.value = "";
                                  }
                                }
                              }}
                            >
                              ×
                            </button>
                          </li>
                        ))}
                      </ul>
                    </div>
                  )}
                </div>
              )}

            <div className="search-refresh-upload">
              <div className="search-input-wrapper-container">
                <div className="search-input-wrapper">
                  {/* eslint-disable-next-line @typescript-eslint/ban-ts-comment */}
                  {/* @ts-ignore */}
                  <FaSearch className="search-icon" />
                  <input
                    type="text"
                    className="search-input"
                    placeholder="Search"
                    value={searchTerm}
                    onChange={handleSearch}
                    aria-label="Search documents"
                  />
                </div>
              </div>

              {/* Flow Name Display */}
              {(flowConfig?.flow_name || currentFlowConfig?.flow_name) && (
                <div className="flow-name-display">
                  <span className="flow-label">Flow:</span>
                  <span className="flow-name">
                    {flowConfig?.flow_name || currentFlowConfig?.flow_name}
                  </span>
                </div>
              )}

              {/* View Toggle */}
              <div className="view-toggle">
                <button
                  type="button"
                  className={`toggle-button ${
                    !isCollapsibleView ? "active" : ""
                  }`}
                  onClick={() => setIsCollapsibleView(false)}
                  title="Table View"
                >
                  📋 List
                </button>
                <button
                  type="button"
                  className={`toggle-button ${
                    isCollapsibleView ? "active" : ""
                  }`}
                  onClick={() => setIsCollapsibleView(true)}
                  title="Status Groups"
                >
                  🗂️ Group
                </button>
              </div>

              <div className="button-group">
                <button
                  className={`refresh-btn ${isRefreshing ? "refreshing" : ""}`}
                  onClick={handleRefresh}
                  disabled={isRefreshing}
                  aria-label="Refresh"
                  style={{
                    display: "flex",
                    alignItems: "center",
                    gap: "8px",
                    padding: "10px 16px",
                    backgroundColor: isRefreshing ? "#f3f4f6" : "white",
                    color: "black",
                    border: "1px solid #ccc",
                    borderRadius: "4px",
                    fontSize: "14px",
                    fontWeight: "400",
                    cursor: isRefreshing ? "not-allowed" : "pointer",
                    transition: "all 0.3s ease",
                    opacity: isRefreshing ? 0.7 : 1
                  }}
                  onMouseEnter={(e) => {
                    if (!isRefreshing) {
                      e.currentTarget.style.backgroundColor = "#f9fafb";
                      e.currentTarget.style.borderColor = "#999";
                      e.currentTarget.style.transform = "translateY(-1px)";
                      e.currentTarget.style.boxShadow =
                        "0 2px 4px rgba(0, 0, 0, 0.1)";
                    }
                  }}
                  onMouseLeave={(e) => {
                    if (!isRefreshing) {
                      e.currentTarget.style.backgroundColor = "white";
                      e.currentTarget.style.borderColor = "#ccc";
                      e.currentTarget.style.transform = "translateY(0)";
                      e.currentTarget.style.boxShadow = "none";
                    }
                  }}
                >
                  {/* eslint-disable-next-line @typescript-eslint/ban-ts-comment */}
                  {/* @ts-ignore */}
                  <MdRefresh
                    className="refresh-icon"
                    style={{
                      fontSize: "20px",
                      animation: isRefreshing
                        ? "spin 1s linear infinite"
                        : "none"
                    }}
                  />
                  {isRefreshing ? "Refreshing..." : "Refresh"}
                </button>
                <button
                  className={`upload-btn ${
                    files.length === 0 ||
                    isUploading ||
                    isMultiUploading ||
                    isBatchUploading ||
                    batchUploadProgress.isUploading
                      ? "disabled"
                      : ""
                  }`}
                  onClick={handleUpload}
                  disabled={
                    files.length === 0 ||
                    isUploading ||
                    isMultiUploading ||
                    isBatchUploading ||
                    batchUploadProgress.isUploading
                  }
                  style={{
                    backgroundColor:
                      files.length === 0 ||
                      isUploading ||
                      isMultiUploading ||
                      isBatchUploading ||
                      batchUploadProgress.isUploading
                        ? "#9ca3af"
                        : undefined,
                    color:
                      files.length === 0 ||
                      isUploading ||
                      isMultiUploading ||
                      isBatchUploading ||
                      batchUploadProgress.isUploading
                        ? "#6b7280"
                        : undefined,
                    cursor:
                      files.length === 0 ||
                      isUploading ||
                      isMultiUploading ||
                      isBatchUploading ||
                      batchUploadProgress.isUploading
                        ? "not-allowed"
                        : "pointer"
                  }}
                  aria-label="Upload document"
                >
                  {/* eslint-disable-next-line @typescript-eslint/ban-ts-comment */}
                  {/* @ts-ignore */}
                  <MdAddCircleOutline className="upload-icon" />
                  {batchUploadProgress.isUploading
                    ? `Uploading batch ${batchUploadProgress.currentBatch}/${batchUploadProgress.totalBatches} (${batchUploadProgress.totalFiles} files)...`
                    : isUploading || isMultiUploading || isBatchUploading
                    ? "Uploading..."
                    : "Upload"}
                </button>
              </div>
            </div>
            {/* Batch Upload Progress Indicator */}
            {batchUploadProgress.isUploading && (
              <div className="batch-upload-progress">
                <div className="progress-info">
                  <p>
                    {/* Processing {batchUploadProgress.totalFiles} files in{" "}
                    {batchUploadProgress.totalBatches} batches of{" "}
                    {UPLOAD_BATCH_SIZE} files */}
                    Upload Progress
                  </p>
                  <p>
                    Current batch: {batchUploadProgress.currentBatch} of{" "}
                    {batchUploadProgress.totalBatches}
                  </p>
                  {batchUploadProgress.totalBatches > 1 && (
                    <p>Please wait while we process your files in batches...</p>
                  )}
                </div>
                <div className="progress-bar">
                  <div
                    className="progress-fill"
                    style={{
                      width: `${
                        batchUploadProgress.totalBatches > 0
                          ? (batchUploadProgress.currentBatch /
                              batchUploadProgress.totalBatches) *
                            100
                          : 0
                      }%`
                    }}
                  ></div>
                </div>
                <p className="progress-percentage">
                  {batchUploadProgress.totalBatches > 0
                    ? Math.round(
                        (batchUploadProgress.currentBatch /
                          batchUploadProgress.totalBatches) *
                          100
                      )
                    : 0}
                  % Complete
                </p>
              </div>
            )}
            {/* Documents Table */}
            {isLoadingDocuments ? (
              <div
                style={{
                  textAlign: "center",
                  padding: "40px 30px",
                  fontSize: "16px",
                  color: "#666"
                }}
              >
                Loading documents...
              </div>
            ) : documentsError ? (
              <div>
                Error loading GDX documents: {documentsError.toString()}
              </div>
            ) : (
              <>
                {isCollapsibleView ? (
                  <CollapsibleDocumentList
                    data={documents}
                    onView={handleViewDocument}
                    onDelete={handleDeleteDocument}
                    onDownload={handleDownloadDocument}
                    isLoading={isLoadingDocuments}
                    error={documentsError}
                    documentType={documentType}
                    flowId={currentFlowConfig?.id || 0}
                    onRefresh={handleRefresh}
                  />
                ) : (
                  <Table
                    data={documents}
                    onView={handleViewDocument}
                    onDelete={handleDeleteDocument}
                    onEdit={handleEditDocument}
                    onApproveView={handleApproveViewDocument}
                    isLoading={isLoadingDocuments}
                    error={documentsError}
                  />
                )}

                {/* Pagination Info - only show in List view when more than 10 documents */}
                {!isCollapsibleView &&
                  documentsData &&
                  (documentsData.totalRecords || 0) > 10 && (
                    <div className="pagination-info">
                      <p>
                        Showing {documents.length} of{" "}
                        {documentsData.totalRecords || 0} documents
                        {paginationParams.search &&
                          ` matching "${paginationParams.search}"`}
                      </p>
                      <div className="page-size-selector">
                        <label htmlFor="pageSize">Show: </label>
                        <select
                          id="pageSize"
                          value={paginationParams.size}
                          onChange={handlePageSizeChange}
                        >
                          <option value={5}>5 per page</option>
                          <option value={10}>10 per page</option>
                          <option value={25}>25 per page</option>
                          <option value={50}>50 per page</option>
                          <option value={100}>100 per page</option>
                        </select>
                      </div>
                    </div>
                  )}
              </>
            )}

            {/* Show pagination based on view type and document count */}
            {!isCollapsibleView && (documentsData?.totalRecords || 0) > 10 && (
              <SimplePagination
                currentPage={
                  documentsData?.currentPage || paginationParams.page
                }
                totalPages={documentsData?.totalPages || 1}
                onPageChange={handlePageChange}
              />
            )}
          </div>
        </div>
      </div>

      {/* Azure Blob Modal */}
      <AzureBlobModal
        isOpen={isAzureBlobModalOpen}
        onClose={() => setIsAzureBlobModalOpen(false)}
        onUpload={handleAzureBlobUpload}
        documentType={documentType}
      />

      {/* Loading Overlay */}
      {isLoadingDocumentView && (
        <div
          style={{
            position: "fixed",
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            backgroundColor: "rgba(0, 0, 0, 0.5)",
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            zIndex: 9999
          }}
        >
          <div
            style={{
              backgroundColor: "white",
              padding: "30px",
              borderRadius: "8px",
              display: "flex",
              flexDirection: "column",
              alignItems: "center",
              gap: "15px"
            }}
          >
            <CircularProgress size={40} />
            <div style={{ fontSize: "16px", color: "#333" }}>
              Loading document details...
            </div>
          </div>
        </div>
      )}

      {/* Document Detail Modal */}
      <DocumentDetailView
        isOpen={isDocumentDetailOpen}
        onClose={() => setIsDocumentDetailOpen(false)}
        documentData={selectedDocumentData}
        currentConfig={selectedDocumentDetailsData}
        currentUser={currentUser}
        onSubmit={handleSubmit}
        onRefetch={async () => {
          // Refetch document details after re-extraction
          if (
            selectedDocumentDetailsData?.id ||
            selectedDocumentDetailsData?.document_id
          ) {
            const documentId =
              selectedDocumentDetailsData.id ||
              selectedDocumentDetailsData.document_id;
            const flowId =
              selectedDocumentDetailsData.flow_id ||
              currentFlowConfig?.id ||
              flowConfig?.flow_id ||
              0;

            try {
              await getGdxDocumentDetail({ documentId, flowId });
              // The query result will automatically update selectedDocumentDetailsData via the useEffect
            } catch (error) {
              console.error("Error refetching document details:", error);
            }
          }
        }}
        activeTab={currentActiveTab}
      />
      <DocumentApprovalPopup
        isOpen={isApprovalOpen}
        onClose={() => setIsApprovalOpen(false)}
        documentData={selectedDocumentData}
        currentConfig={selectedDocumentDetailsData}
        onApprove={handleApprove}
        onReject={handleReject}
      />
    </>
  );
};

export default UploadGDX;
